/* =============================================================
   AddStudentModal.jsx — Add single student modal
   -------------------------------------------------------------
   Modal form for manually adding a new incoming or outgoing
   student. Adapts its fields based on the type prop:

   OUTGOING:
     • Destination University = required dropdown from partner_universities.
       If no partners exist, an info banner is shown and the form cannot
       be submitted — the IO must create a partner university first.
     • Selecting a partner also stores its name as destination_university
       so list views and exports still have a plain-text label.
     • Exchange Type = Erasmus / Bilateral / Double Degree

   INCOMING:
     • Home University = dropdown from partner_universities (the university
       the student is coming from, linked to a formal exchange agreement).
     • Exchange Type = Erasmus / Bilateral / Double Degree
     • eu_status auto-computed from nationality (EU set defined in countries.js)
   ============================================================= */

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { NATIONALITIES, getEuStatus } from '../lib/countries';
import { DEGREE_LEVELS } from '../lib/studentCategory';

const EXCHANGE_TYPES = [
  { value: 'erasmus',       label: 'Erasmus'        },
  { value: 'bilateral',     label: 'Bilateral'      },
  { value: 'double_degree', label: 'Double Degree'  },
];

/* ─── ADD STUDENT MODAL COMPONENT ───────────────────────── */

export default function AddStudentModal({ type, onAdd, onClose }) {
  const isIncoming = type === 'incoming';

  const [form, setForm] = useState({
    name:              '',
    uni:               '',   // home_university  (incoming — from partner dropdown)
    dest:              '',   // destination_university (outgoing — set from partner selection)
    nationality:       '',
    exchangeType:      '',
    degreeLevel:       '',
    degreeLevelCustom: '',
    partnerId:         null, // FK to partner_universities.id
    semesterId:        null, // FK to semesters.id
  });
  const [errors,        setErrors]        = useState({});
  const [saving,        setSaving]        = useState(false);
  const [done,          setDone]          = useState(false);
  const [submitError,   setSubmitError]   = useState(null);
  const [partners,      setPartners]      = useState([]);
  const [partnersReady, setPartnersReady] = useState(false);
  const [semesters,     setSemesters]     = useState([]);

  /* ── Load partner universities and semesters ────────────── */
  useEffect(() => {
    supabase
      .from('partner_universities')
      .select('id, name, abbreviation, country')
      .order('name', { ascending: true })
      .then(({ data }) => {
        setPartners(data ?? []);
        setPartnersReady(true);
      });
    supabase
      .from('semesters')
      .select('id, name')
      .order('created_at', { ascending: false })
      .then(({ data }) => setSemesters(data ?? []));
  }, []);

  /* close on Escape */
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: '' }));
  };

  /* ── Handle partner dropdown selection ─────────────────── */
  const handlePartnerSelect = (e) => {
    const id       = e.target.value || null;
    const selected = partners.find(p => p.id === id) ?? null;

    setForm(f => ({
      ...f,
      partnerId: id,
      // OUTGOING: sync dest with the chosen partner name.
      // INCOMING: set home uni name from selected partner.
      dest: !isIncoming ? (selected?.name ?? '') : f.dest,
      uni:  isIncoming  ? (selected?.name ?? '') : f.uni,
    }));
    setErrors(err => ({ ...err, partnerId: '' }));
  };

  /* ── Validation ─────────────────────────────────────────── */
  const validate = () => {
    const err = {};
    if (!form.name.trim())    err.name         = 'Name is required';
    // Outgoing: destination university is mandatory.
    // Incoming: home university is optional (student may not be tied to a formal partner).
    if (!isIncoming && !form.partnerId) err.partnerId = 'Select a destination university';
    if (!form.nationality)    err.nationality  = 'Nationality is required';
    if (!form.exchangeType)   err.exchangeType = 'Exchange type is required';
    return err;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (Object.keys(err).length) { setErrors(err); return; }

    const euStatus = getEuStatus(form.nationality);

    setSaving(true);
    setSubmitError(null);
    try {
      await onAdd({ ...form, euStatus, semesterId: form.semesterId || null });
      setDone(true);
      setTimeout(onClose, 900);
    } catch (e) {
      const msg = e?.message ?? e?.error_description ?? JSON.stringify(e);
      setSubmitError(msg);
    } finally {
      setSaving(false);
    }
  };

  /* ── Can submit? ────────────────────────────────────────── */
  const noPartnersOutgoing = !isIncoming && partnersReady && partners.length === 0;

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Modal card */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px] text-indigo-600">person_add</span>
            <h2 className="font-display text-base font-semibold text-[#0b1c30]">
              Add {isIncoming ? 'Incoming' : 'Outgoing'} Student
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">

          {/* Full Name */}
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">
              Full Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="e.g. Maria Gonzalez"
              className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] transition
                ${errors.name ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
            />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          {/* ── University dropdown (incoming = Home Uni, outgoing = Destination) ── */}
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">
              {isIncoming ? 'Home University' : 'Destination University'}{' '}
              <span className="text-red-400">*</span>
            </label>

            {/* No partners yet → info banner (outgoing only) */}
            {noPartnersOutgoing ? (
              <div className="flex items-start gap-2.5 p-3.5 bg-amber-50 border border-amber-200 rounded-xl">
                <span className="material-symbols-outlined text-[18px] text-amber-500 flex-shrink-0 mt-0.5">info</span>
                <p className="text-xs text-amber-700 leading-relaxed">
                  No partner universities have been created yet.
                  Go to <strong>University Partners</strong> and add at least one before registering an outgoing student.
                </p>
              </div>
            ) : (
              <select
                value={form.partnerId ?? ''}
                onChange={handlePartnerSelect}
                className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-[#0b1c30] transition cursor-pointer
                  ${errors.partnerId ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
              >
                <option value="">
                  {isIncoming ? '— Not linked to a partner —' : 'Select destination university…'}
                </option>
                {partners.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.abbreviation ? ` (${p.abbreviation})` : ''}{p.country ? ` — ${p.country}` : ''}
                  </option>
                ))}
              </select>
            )}
            {errors.partnerId && <p className="text-xs text-red-500 mt-1">{errors.partnerId}</p>}
          </div>

          {/* Nationality + Exchange Type row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">
                Nationality <span className="text-red-400">*</span>
              </label>
              <select
                value={form.nationality}
                onChange={e => set('nationality', e.target.value)}
                className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-[#0b1c30] transition cursor-pointer
                  ${errors.nationality ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
              >
                <option value="">Select nationality</option>
                {NATIONALITIES.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              {errors.nationality && <p className="text-xs text-red-500 mt-1">{errors.nationality}</p>}
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">
                Exchange Type <span className="text-red-400">*</span>
              </label>
              <select
                value={form.exchangeType}
                onChange={e => set('exchangeType', e.target.value)}
                className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-[#0b1c30] transition cursor-pointer
                  ${errors.exchangeType ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
              >
                <option value="">Select type</option>
                {EXCHANGE_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              {errors.exchangeType && <p className="text-xs text-red-500 mt-1">{errors.exchangeType}</p>}
            </div>
          </div>

          {/* Degree Level */}
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">
              Degree Level
            </label>
            <div className="flex gap-2">
              <select
                value={form.degreeLevel}
                onChange={e => set('degreeLevel', e.target.value)}
                className="flex-1 px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-[#0b1c30] transition cursor-pointer"
              >
                <option value="">Not specified</option>
                {DEGREE_LEVELS.map(dl => (
                  <option key={dl.value} value={dl.value}>{dl.label}</option>
                ))}
              </select>
              {form.degreeLevel === 'other' && (
                <input
                  type="text"
                  value={form.degreeLevelCustom}
                  onChange={e => set('degreeLevelCustom', e.target.value)}
                  placeholder="Specify degree type…"
                  className="flex-1 px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] transition"
                />
              )}
            </div>
          </div>

          {/* Semester */}
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">
              Exchange Term
            </label>
            {semesters.length === 0 ? (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <span className="material-symbols-outlined text-[16px] text-amber-500 flex-shrink-0 mt-0.5">info</span>
                <p className="text-xs text-amber-700">
                  No exchange terms created yet. Go to <strong>Settings → Exchange Terms</strong> to create one.
                </p>
              </div>
            ) : (
              <select
                value={form.semesterId ?? ''}
                onChange={e => set('semesterId', e.target.value || null)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-[#0b1c30] transition cursor-pointer"
              >
                <option value="">— Not assigned —</option>
                {semesters.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
          </div>

          {/* EU status auto-indicator */}
          {form.nationality && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium ${
              getEuStatus(form.nationality) === 'eu'
                ? 'bg-blue-50 text-blue-700 border border-blue-100'
                : 'bg-amber-50 text-amber-700 border border-amber-100'
            }`}>
              <span className="material-symbols-outlined text-[14px]">
                {getEuStatus(form.nationality) === 'eu' ? 'verified' : 'public'}
              </span>
              {getEuStatus(form.nationality) === 'eu'
                ? 'EU nationality — standard document set applies'
                : 'Non-EU nationality — additional documents may be required'}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
          {/* Inline error */}
          {submitError && (
            <p className="text-xs text-red-500 flex items-center gap-1 flex-1 min-w-0 truncate">
              <span className="material-symbols-outlined text-[13px] flex-shrink-0">error</span>
              {submitError}
            </p>
          )}
          {!submitError && <span />}
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || noPartnersOutgoing}
              className={`px-5 py-2.5 text-sm font-medium rounded-xl transition-all flex items-center gap-2 ${
                done
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                  : 'bg-[#4343d5] text-white hover:bg-indigo-700 disabled:opacity-60'
              }`}
            >
              <span className="material-symbols-outlined text-[16px]">
                {done ? 'check_circle' : saving ? 'hourglass_empty' : 'person_add'}
              </span>
              {done ? 'Added!' : saving ? 'Adding...' : 'Add Student'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
